import { pageTitle } from '@palaccount/config';
import { CATEGORY_COLOURS, WAREHOUSE_MESSAGES, type WarehouseResponse } from '@palaccount/contracts';
import { Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { CurrencyField } from '@/components/ui/currency-field';
import { currencyNotOffered, useCurrencyPickerCodes } from '@/features/fx/api';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import { InventoryTabs } from './inventory-tabs';
import { Chip } from './stock-chips';
import { useLegalEntities, useLegalEntityMutations } from './api';
import { useWarehouseMutations, useWarehouses } from './stock-api';

const WAREHOUSE_TYPES = ['main', 'branch', 'retail', 'distribution', 'virtual', 'three_pl'] as const;
const VALUATION_METHODS = ['fifo', 'weighted_average', 'last_cost'] as const;
const CAPACITY_UNITS = ['pallet', 'sqft', 'sqm'] as const;
const ZONE_TYPES = ['bulk', 'cold', 'pick', 'hazmat', 'other'] as const;
/** INV-04 §5.4's starter list — a chip toggle, not a typed field. */
const CARRIERS = ['DHL Express', 'FedEx', 'UPS', 'Aramex', 'Local courier'] as const;

const CAPACITY_RE = /^\d{1,15}$/;

interface ZoneRow {
  code: string;
  name: string;
  type: (typeof ZONE_TYPES)[number];
  capacity: string;
  /** What the SERVER says points at this zone — the delete refusal's ammunition. */
  binCount: number;
  /**
   * The code THIS ZONE'S BINS currently carry, which is not always `code`:
   * mid-rename the field can be blank or half-typed, and a bin must not follow
   * it there. `renameZoneOnBins` keeps this pointing at the last code the bins
   * were actually moved to, which is what lets a clear-then-retype find them
   * again (BR-INV-04-09).
   */
  boundCode: string;
}

interface BinRow {
  code: string;
  /** '' = unassigned; joins its zone by code within the payload (BR-INV-04-09). */
  zoneCode: string;
  capacity: string;
}

interface FormState {
  code: string;
  name: string;
  type: (typeof WAREHOUSE_TYPES)[number];
  legalEntityId: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  manager: string;
  phone: string;
  email: string;
  openingHours: string;
  currency: string;
  valuationMethod: (typeof VALUATION_METHODS)[number];
  allowNegativeStock: boolean;
  includeInAvailable: boolean;
  capacity: string;
  capacityUnit: string;
  useBins: boolean;
  carriers: string[];
  leadTimeDays: string;
  allowTransfers: boolean;
  isDefault: boolean;
  /** IT-L4: '' = none; else #rrggbb. */
  colour: string;
}

const BLANK: FormState = {
  code: '',
  name: '',
  type: 'branch',
  legalEntityId: '',
  street: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  manager: '',
  phone: '',
  email: '',
  openingHours: '',
  currency: '',
  valuationMethod: 'weighted_average',
  allowNegativeStock: false,
  includeInAvailable: true,
  capacity: '',
  capacityUnit: '',
  useBins: false,
  carriers: [],
  leadTimeDays: '',
  allowTransfers: true,
  isDefault: false,
  colour: '',
};

function fromWarehouse(warehouse: WarehouseResponse): FormState {
  return {
    code: warehouse.code,
    name: warehouse.name,
    type: warehouse.type,
    legalEntityId: warehouse.legalEntityId,
    street: warehouse.street ?? '',
    city: warehouse.city ?? '',
    region: warehouse.region ?? '',
    postalCode: warehouse.postalCode ?? '',
    country: warehouse.country ?? '',
    manager: warehouse.manager ?? '',
    phone: warehouse.phone ?? '',
    email: warehouse.email ?? '',
    openingHours: warehouse.openingHours ?? '',
    currency: warehouse.currency,
    valuationMethod: warehouse.valuationMethod,
    allowNegativeStock: warehouse.allowNegativeStock,
    includeInAvailable: warehouse.includeInAvailable,
    capacity: warehouse.capacity ?? '',
    capacityUnit: warehouse.capacityUnit ?? '',
    useBins: warehouse.useBins,
    carriers: [...warehouse.carriers],
    leadTimeDays: warehouse.leadTimeDays === null ? '' : String(warehouse.leadTimeDays),
    allowTransfers: warehouse.allowTransfers,
    isDefault: warehouse.isDefault,
    colour: warehouse.colour ?? '',
  };
}

/** A labelled switch — the tenant reads the hint, not the field name. */
function SwitchField({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <div className="grid gap-1">
      <Label htmlFor={id} className="flex items-center gap-2 font-normal">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(next) => onChange(next === true)}
        />
        {label}
      </Label>
      {hint ? <p className="ps-6 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    // Default CLOSED: the optional depth of INV-04 §5.4 stays out of the way
    // until a larger operation goes looking for it.
    <details className="rounded-xl border">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
        {title} <span className="font-normal text-muted-foreground">— {hint}</span>
      </summary>
      <div className="grid gap-4 border-t p-4">{children}</div>
    </details>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  disabled,
  hint,
  placeholder,
  type,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean | undefined;
  hint?: string | undefined;
  placeholder?: string | undefined;
  type?: string | undefined;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        {...(type ? { type } : {})}
        {...(placeholder ? { placeholder } : {})}
        value={value}
        disabled={disabled ?? false}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/**
 * INV-04 / MD-08 — the location master as a master-detail screen: the list of
 * places on the left, one place's whole record on the right.
 *
 * The rules that show on screen: the code goes blank-means-Auto on create and
 * locks once the location has movements (BR-MD-08-03); a Virtual location
 * says out loud that nothing physical sits there; bins can be switched off
 * WITHOUT losing their rows (BR-INV-04-11); a zone still holding bins refuses
 * to go (BR-INV-04-09); and Delete answers a 409 with the amber banner and a
 * Deactivate instead button (BR-INV-04-02/-04).
 */
export function WarehousesPage() {
  const { t } = useTranslation();
  const { data: warehouses, isPending, isError } = useWarehouses({});
  const { create, update, setActive, remove } = useWarehouseMutations();
  // MD-08 field 4's Companies list; inactive included so an owner an entity
  // was switched off under still DISPLAYS on its warehouse.
  const { data: legalEntities } = useLegalEntities(true);

  const canCreate = useCan('inventory.create');
  const canEdit = useCan('inventory.edit');

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  // MD-05: a new warehouse is in the company's own money (GEN-107), read from
  // the session — `BLANK` is a module constant and cannot, so it holds '' and
  // the base is stamped on at every point the form is opened blank.
  const baseCurrency = useBaseCurrency();
  const blank = (): FormState => ({ ...BLANK, currency: baseCurrency });
  const [form, setForm] = useState<FormState>(blank);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [bins, setBins] = useState<BinRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** ERR-MD-08-05 — ticking a second receiving default unticks the first, AFTER ASKING. */
  const [confirmingDefault, setConfirmingDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('wh.title'));
  }, [t]);

  const selected = useMemo(
    () =>
      selectedId && selectedId !== 'new'
        ? (warehouses ?? []).find((row) => row.id === selectedId)
        : undefined,
    [warehouses, selectedId],
  );

  /*
   * THE SAVED CURRENCY, NOT `form.currency`, and the difference is the whole
   * warning.
   *
   * `useCurrencyPickerCodes` appends the value it is handed when the table no
   * longer offers it, so feeding it the LIVE field made every code the user
   * typed instantly "one of ours" and the warning could never fire. Handed the
   * PERSISTED value instead, the list still carries a lawfully-held
   * switched-off code (BR-MD-01-07) while a freshly typed typo is genuinely
   * absent from it — which is exactly the pair `currencyNotOffered` has to
   * tell apart.
   */
  const currencyCodes = useCurrencyPickerCodes(selected?.currency);

  /**
   * The location that would be UNTICKED by making this one the default — the
   * "Central Distribution Center" of ERR-MD-08-05's sentence. Excludes the
   * record being edited: re-ticking the default on the default itself is not
   * "a second". (The tiers screen's `currentDefault`, location-shaped.)
   */
  const currentDefault = (warehouses ?? []).find(
    (row) => row.isDefault && row.id !== (selected?.id ?? ''),
  );

  // Pick the first location once the list arrives — the detail pane is the
  // screen, an empty one is a dead end.
  useEffect(() => {
    if (selectedId === null && warehouses && warehouses.length > 0) {
      setSelectedId(warehouses[0]!.id);
    }
  }, [warehouses, selectedId]);

  useEffect(() => {
    if (!selected) return;
    const stamp = `${selected.id}:${selected.updatedAt}`;
    if (loaded.current === stamp) return;
    loaded.current = stamp;
    setForm(fromWarehouse(selected));
    setZones(
      selected.zones.map((zone) => ({
        code: zone.code,
        name: zone.name ?? '',
        type: zone.type,
        capacity: zone.capacity ?? '',
        binCount: zone.binCount,
        // The stored bins already point at this code — see `renameZoneOnBins`.
        boundCode: zone.code,
      })),
    );
    setBins(
      selected.bins.map((bin) => ({
        code: bin.code,
        zoneCode: bin.zoneCode ?? '',
        capacity: bin.capacity ?? '',
      })),
    );
    setError(null);
    setDeleteBlocked(null);
    setConfirmingDefault(false);
  }, [selected]);

  const isNew = selectedId === 'new';
  const editable = isNew ? canCreate : canEdit;

  /**
   * ERR-INV-04-05 — the list search box FILTERS. In the prototype it was
   * decorative: a user typed and nothing happened, with no empty state and no
   * error to explain it, which is the worst shape a dead control can take. It
   * matches code, name and city, case-insensitively; a blank box is no
   * constraint rather than no rows.
   */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = warehouses ?? [];
    if (needle === '') return rows;
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(needle) ||
        row.code.toLowerCase().includes(needle) ||
        (row.city ?? '').toLowerCase().includes(needle),
    );
  }, [warehouses, search]);

  const startNew = () => {
    loaded.current = null;
    setSelectedId('new');
    setForm(blank());
    setZones([]);
    setBins([]);
    setError(null);
    setDeleteBlocked(null);
    setConfirmingDefault(false);
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const zoneCodes = zones.map((zone) => zone.code.trim()).filter((code) => code !== '');

  /**
   * BR-INV-04-09, the half that is not a refusal: "Renaming a zone must carry
   * its bins with it." ERR-INV-04-03 is what happens when it does not — in the
   * prototype the bins silently lost their zone, which is the shape of defect
   * this project keeps finding worst, because nothing on screen changes.
   *
   * A bin names its zone by CODE inside the save payload, so the two halves of
   * that payload have to be renamed together. Without this the save is not
   * silent — the server refuses it with `binZoneMissing` naming the bin — but
   * the user is then asked to retype a link they never broke, and a refusal is
   * not what the rule asks for. Renaming here keeps the server's refusal as
   * the backstop it should be rather than the everyday path.
   *
   * Matching is on `boundCode` — the code the bins actually carry — rather
   * than on the field's live value, and that distinction is the whole of the
   * edge case. A rename arrives one keystroke at a time, so the box passes
   * through half-typed values and, after Clear, through the empty string. A
   * BLANK new code is never written onto a bin: that would make it "Any /
   * unassigned", which is the silent loss of exactly the link this rule
   * protects, arriving from the fix rather than from the defect. Holding
   * `boundCode` still through the blank is what lets the bins rejoin the zone
   * when the new code is typed.
   *
   * A brand-new zone ADOPTS its first non-empty code without moving anything:
   * bins carrying '' are unassigned by choice and are not this zone's.
   *
   * Two zones sharing one code would move both sets of bins — but that state
   * is already refused by BR-INV-04-10, so it cannot be saved either way.
   */
  const renameZoneOnBins = (index: number, to: string) => {
    const after = to.trim();
    if (after === '') return;
    const before = zones[index]?.boundCode.trim() ?? '';
    if (before === after) return;
    setZones((current) =>
      current.map((zone, at) => (at === index ? { ...zone, boundCode: after } : zone)),
    );
    if (before === '') return; // adoption, not a rename — nothing points here yet
    setBins((current) =>
      current.map((bin) => (bin.zoneCode === before ? { ...bin, zoneCode: after } : bin)),
    );
  };

  const removeZone = (index: number) => {
    const zone = zones[index];
    if (!zone) return;
    const localBins = bins.filter((bin) => bin.zoneCode === zone.code).length;
    const held = Math.max(zone.binCount, localBins);
    if (held > 0) {
      // BR-INV-04-09 — refused here so the trip to the server is never made.
      //
      // PARAMETERISED, and doubly so: the sentence names the zone AND counts
      // the bins, and the contract picks "bin"/"bins" in English. No exact-match
      // map entry can exist for it, so this is an ICU key with a plural, which
      // is the only form that can carry Arabic's six categories — English's two
      // would flatten five of them into one wrong ending.
      setError(t('wh.errZoneHasBins', { code: zone.code, bins: held }));
      return;
    }
    setZones(zones.filter((_, at) => at !== index));
  };

  const handleFailure = (thrown: unknown, permission: 'create' | 'edit' | 'delete') => {
    if (thrown instanceof ApiError && thrown.status === 403) {
      denyPermission(`inventory.${permission}`);
    }
    // The cause rides along: a structured refusal (D46 — codeTaken,
    // codeLocked, valuationLocked, stillHoldsStock, zone/bin duplicates)
    // carries key+params, so the sentence reaches an Arabic reader in Arabic.
    setError(
      localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
    );
  };

  const validate = (): string | null => {
    if (form.name.trim() === '') return localizeServerMessage(t, WAREHOUSE_MESSAGES.codeAndName);
    // ERR-INV-04-04 — Total capacity is a NUMBER. The prototype accepted any
    // text ("eighteen thousand" sat happily beside 18,000), so the two could
    // never be compared or added. `CAPACITY_RE` is digits only, matching the
    // `numeric(15,0)` column behind it; the zone rows below get the same test,
    // because §5.2's zone capacity is the same field one table down.
    if (form.capacity.trim() !== '' && !CAPACITY_RE.test(form.capacity.trim())) {
      return t('wh.errCapacity');
    }
    if (!/^[A-Za-z]{3}$/.test(form.currency.trim())) return t('wh.errCurrency');
    for (const zone of zones) {
      if (zone.capacity.trim() !== '' && !CAPACITY_RE.test(zone.capacity.trim())) {
        return t('wh.errCapacity');
      }
    }
    return null;
  };

  const body = () => {
    const text = (value: string) => (value.trim() === '' ? null : value.trim());
    return {
      name: form.name.trim(),
      type: form.type,
      // MD-08 field 4: '' = let the server set the sole entity silently.
      ...(form.legalEntityId === '' ? {} : { legalEntityId: form.legalEntityId }),
      street: text(form.street),
      city: text(form.city),
      region: text(form.region),
      postalCode: text(form.postalCode),
      country: text(form.country),
      manager: text(form.manager),
      phone: text(form.phone),
      email: text(form.email),
      openingHours: text(form.openingHours),
      capacity: text(form.capacity),
      capacityUnit:
        form.capacityUnit === ''
          ? null
          : (form.capacityUnit as (typeof CAPACITY_UNITS)[number]),
      currency: form.currency.trim().toUpperCase(),
      valuationMethod: form.valuationMethod,
      allowNegativeStock: form.allowNegativeStock,
      includeInAvailable: form.includeInAvailable,
      useBins: form.useBins,
      carriers: form.carriers,
      leadTimeDays: form.leadTimeDays.trim() === '' ? null : Number(form.leadTimeDays.trim()),
      allowTransfers: form.allowTransfers,
      isDefault: form.isDefault,
      // IT-L4: an emptied colour takes it off (the update contract takes null).
      colour: form.colour === '' ? null : form.colour,
      // Replace-sets; a bin names its zone by CODE and the server resolves it.
      zones: zones
        .filter((zone) => zone.code.trim() !== '')
        .map((zone) => ({
          code: zone.code.trim(),
          name: text(zone.name),
          type: zone.type,
          capacity: text(zone.capacity),
        })),
      bins: bins
        .filter((bin) => bin.code.trim() !== '')
        .map((bin) => ({
          code: bin.code.trim(),
          zoneCode: bin.zoneCode.trim() === '' ? null : bin.zoneCode.trim(),
          capacity: text(bin.capacity),
        })),
    };
  };

  const onSave = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        // Field 1: a blank code IS the Auto behaviour — the server allocates.
        const created = await create.mutateAsync({
          ...body(),
          ...(form.code.trim() === '' ? {} : { code: form.code.trim() }),
        });
        loaded.current = null;
        setSelectedId(created.id);
      } else if (selected) {
        await update.mutateAsync({
          warehouseId: selected.id,
          body: {
            ...body(),
            ...(form.code.trim() === '' || selected.hasMovements
              ? {}
              : { code: form.code.trim() }),
          },
        });
        loaded.current = null;
      }
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'create' : 'edit');
    } finally {
      setBusy(false);
    }
  };

  const onToggleActive = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await setActive.mutateAsync({ warehouseId: selected.id, active: !selected.isActive });
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'edit');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!selected) return;
    setBusy(true);
    setError(null);
    setDeleteBlocked(null);
    try {
      await remove.mutateAsync(selected.id);
      loaded.current = null;
      setSelectedId(null);
    } catch (thrown) {
      // BR-INV-04-02: the refusal is the amber banner, verbatim, and it comes
      // with the thing the tenant should do instead. Localized by CAUSE
      // (D46): both delete refusals — the banner and BR-MD-08-05's
      // movement-count sentence — arrive structured, and the fallback is the
      // server's own English sentence, never a hole.
      if (thrown instanceof ApiError && thrown.status === 409) {
        setDeleteBlocked(localizeServerMessage(t, thrown.message, thrown));
      } else {
        handleFailure(thrown, 'delete');
      }
    } finally {
      setBusy(false);
    }
  };

  const onDeactivateInstead = async () => {
    setDeleteBlocked(null);
    await onToggleActive();
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('wh.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createWarehouses">
          <Button onClick={startNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* ── The list of places ──────────────────────────────────────── */}
        <Card className="h-fit">
          <CardContent className="grid gap-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('wh.searchLabel')}
                placeholder={t('wh.searchPh')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            {isPending ? (
              <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
            ) : isError ? (
              <p role="alert" className="py-6 text-center text-sm text-destructive">
                {t('common.loadFailed')}
              </p>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {(warehouses ?? []).length === 0 ? t('wh.empty') : t('wh.noMatch')}
              </p>
            ) : (
              <ul className="grid gap-1">
                {filtered.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      aria-current={row.id === selectedId}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-start transition-colors hover:bg-muted',
                        row.id === selectedId && 'bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'size-2 shrink-0 rounded-full',
                          row.isActive ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                        )}
                        aria-hidden
                      />
                      <span className="grid min-w-0">
                        <span className="truncate text-sm font-medium">{row.name}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          <Ltr value={row.code} className="font-mono" /> ·{' '}
                          {t(`wh.type.${row.type}`)}
                        </span>
                      </span>
                      {row.isActive ? null : (
                        <span className="ms-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {t('common.inactive')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* ── One place's record ──────────────────────────────────────── */}
        {selectedId === null ? (
          <Card>
            <CardContent className="py-16 text-center text-sm text-muted-foreground">
              {t('wh.selectPrompt')}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {/* MD-08 fields 23–25 — computed live on every read, never stored. */}
            {selected ? (
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="grid gap-1 py-4">
                    <span className="text-xs text-muted-foreground">{t('wh.cardItems')}</span>
                    <span className="font-mono text-2xl font-bold">{selected.itemsHeld}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="grid gap-1 py-4">
                    <span className="text-xs text-muted-foreground">{t('wh.cardValue')}</span>
                    <Amount
                      value={selected.stockValue}
                      currency={selected.currency}
                      className="text-2xl font-bold"
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="grid gap-1 py-4">
                    <span className="text-xs text-muted-foreground">{t('wh.cardLow')}</span>
                    <span
                      className={cn(
                        'font-mono text-2xl font-bold',
                        selected.lowStockCount > 0 && 'text-amber-700 dark:text-amber-400',
                      )}
                    >
                      {selected.lowStockCount}
                    </span>
                  </CardContent>
                </Card>
              </div>
            ) : null}

            {deleteBlocked ? (
              <div className="grid gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <p>{deleteBlocked}</p>
                {/*
                  BR-INV-04-03: "the delete-prevention banner must offer
                  Deactivate instead as its PRIMARY action, so a refused delete
                  always has a next step." The ordering here is the rule, not
                  house style — Deactivate carries the default variant and comes
                  first, Dismiss is `outline` and comes second. Swap the two
                  variants and the banner still offers the same two actions
                  while telling the user the way out is to close it, which is a
                  refusal with no next step: exactly what the rule forbids.
                */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => void onDeactivateInstead()} disabled={busy}>
                    {t('wh.deactivateInstead')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDeleteBlocked(null)}>
                    {t('wh.dismiss')}
                  </Button>
                </div>
              </div>
            ) : null}

            {error ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              >
                {error}
              </p>
            ) : null}

            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center gap-3">
                <CardTitle className="me-auto">
                  {t(isNew ? 'wh.newTitle' : 'wh.editTitle')}
                </CardTitle>
                {!isNew && selected ? (
                  <Gated permission="inventory.edit" reasonKey="perm.cannot.editWarehouses">
                    <Button variant="outline" onClick={() => void onToggleActive()} disabled={busy}>
                      {t(selected.isActive ? 'wh.deactivate' : 'wh.reactivate')}
                    </Button>
                  </Gated>
                ) : null}
                {!isNew && selected ? (
                  <>
                    <Gated permission="inventory.delete" reasonKey="perm.cannot.deleteWarehouses">
                      <Button
                        variant="destructive"
                        onClick={() => setDeleting(true)}
                        disabled={busy}
                      >
                        <Trash2 aria-hidden /> {t('common.delete')}
                      </Button>
                    </Gated>
                    {/*
                      ERR-INV-04-02 / BR-INV-04-04 — an EMPTY location asked
                      nothing in the prototype: it vanished on the click, with
                      no confirmation and nothing to undo it. The button only
                      opens this box; `onDelete` is reachable from its Confirm
                      alone, so the refusal path (BR-INV-04-02's banner, for a
                      location that still holds stock) is what a NON-empty one
                      meets afterwards and the confirmation is what every
                      location meets first.

                      BR-MD-08-13 is the same requirement stated from MD-08's
                      side — "A warehouse with no references must still ask
                      before deleting, through the shared confirm dialog in
                      §4.20. No master record may be deleted silently." — and
                      this box is where it is met, for the empty case too.
                    */}
                    <DeleteConfirmBox
                      open={deleting}
                      detail={t('wh.confirmDelete', { name: selected.name })}
                      onCancel={() => setDeleting(false)}
                      onConfirm={() => void onDelete()}
                      busy={busy}
                    />
                  </>
                ) : null}
                <Gated
                  permission={isNew ? 'inventory.create' : 'inventory.edit'}
                  reasonKey={
                    isNew ? 'perm.cannot.createWarehouses' : 'perm.cannot.editWarehouses'
                  }
                >
                  <Button onClick={() => void onSave()} disabled={busy}>
                    {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                    {t('common.save')}
                  </Button>
                </Gated>
              </CardHeader>
              <CardContent className="grid gap-5">
                {/* ── Core identification ──────────────────────────────── */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <TextField
                    id="wh-code"
                    label={t('wh.fCode')}
                    value={form.code}
                    onChange={(next) => set('code', next)}
                    placeholder={t('wh.codeAuto')}
                    disabled={!editable || Boolean(selected?.hasMovements)}
                    {...(selected?.hasMovements ? { hint: t('wh.codeLockedHint') } : {})}
                  />
                  <TextField
                    id="wh-name"
                    label={t('wh.fName')}
                    value={form.name}
                    onChange={(next) => set('name', next)}
                    disabled={!editable}
                  />
                  <div className="grid gap-1.5">
                    <Label htmlFor="wh-type">{t('wh.fType')}</Label>
                    <NativeSelect
                      id="wh-type"
                      value={form.type}
                      disabled={!editable}
                      onChange={(event) =>
                        set('type', event.target.value as FormState['type'])
                      }
                    >
                      {WAREHOUSE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`wh.type.${type}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
                {form.type === 'virtual' ? (
                  <p className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                    {t('wh.virtualNote')}
                  </p>
                ) : null}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* MD-08 field 4 — "Pick from list (Companies)". Hidden
                      when the tenant has exactly ONE entity: "hidden and set
                      silently when there is only one". */}
                  {(legalEntities ?? []).length > 1 ? (
                    <div className="grid gap-1.5">
                      <Label htmlFor="wh-legal">{t('wh.fLegalEntity')}</Label>
                      <NativeSelect
                        id="wh-legal"
                        value={form.legalEntityId}
                        onChange={(event) => set('legalEntityId', event.target.value)}
                        disabled={!editable}
                      >
                        <option value="">—</option>
                        {(legalEntities ?? [])
                          .filter((entity) => entity.isActive || entity.id === form.legalEntityId)
                          .map((entity) => (
                            <option key={entity.id} value={entity.id}>
                              {entity.name}
                            </option>
                          ))}
                      </NativeSelect>
                    </div>
                  ) : null}
                  <TextField
                    id="wh-street"
                    label={t('wh.fStreet')}
                    value={form.street}
                    onChange={(next) => set('street', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-city"
                    label={t('wh.fCity')}
                    value={form.city}
                    onChange={(next) => set('city', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-region"
                    label={t('wh.fRegion')}
                    value={form.region}
                    onChange={(next) => set('region', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-postal"
                    label={t('wh.fPostalCode')}
                    value={form.postalCode}
                    onChange={(next) => set('postalCode', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-country"
                    label={t('wh.fCountry')}
                    value={form.country}
                    onChange={(next) => set('country', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-manager"
                    label={t('wh.fManager')}
                    value={form.manager}
                    onChange={(next) => set('manager', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-phone"
                    label={t('wh.fPhone')}
                    value={form.phone}
                    onChange={(next) => set('phone', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-email"
                    label={t('wh.fEmail')}
                    type="email"
                    value={form.email}
                    onChange={(next) => set('email', next)}
                    disabled={!editable}
                  />
                  <TextField
                    id="wh-hours"
                    label={t('wh.fOpeningHours')}
                    value={form.openingHours}
                    onChange={(next) => set('openingHours', next)}
                    disabled={!editable}
                  />
                </div>
                {/*
                  MD-RULE-5's showing half — all four facts, now that
                  20260902100000 gave `warehouses` its `updated_by` and the
                  wire carries the authorship pair. This replaced the
                  date-only `ChangedOnLine` interim the previous wave shipped
                  while the wire reached no further.
                */}
                {!isNew && selected ? <AuthorshipLine record={selected} /> : null}
              </CardContent>
            </Card>

            {/* ── Stock behaviour (BR-INV-04-06/-07/-08) ────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle>{t('wh.secStock')}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* MD-08's "Short text", with the tenant's own codes offered
                      under it. The value is still whatever is typed — the
                      screen's own /^[A-Za-z]{3}$/ check below is the shape
                      rule, and BR-DATA-06 is the server's. */}
                  <div className="grid gap-1.5">
                    <Label htmlFor="wh-currency">{t('wh.fCurrency')}</Label>
                    <CurrencyField
                      id="wh-currency"
                      label={t('wh.fCurrency')}
                      value={form.currency}
                      onChange={(next) => set('currency', next)}
                      options={currencyCodes}
                      disabled={!editable}
                    />
                    {/*
                      A WARNING, NOT A BLOCK, and told BEFORE the save rather
                      than refused after it. Save stays enabled and the value
                      stays whatever was typed — that is what keeps MD-08's
                      "Short text" true while removing the silence the strict
                      guard would otherwise turn into a refusal.

                      It stays quiet for a currency this warehouse already
                      HOLDS, even one the tenant has since switched off,
                      because `currencyCodes` carries the held value
                      (BR-MD-01-07). That is the property that makes this a
                      typo warning rather than a scold — see
                      `currencyNotOffered`.
                    */}
                    {currencyNotOffered(form.currency, currencyCodes) ? (
                      <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                        {t('fx.currencyNotYours', { code: form.currency.trim().toUpperCase() })}
                      </p>
                    ) : null}
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="wh-valuation">{t('wh.fValuation')}</Label>
                    {/* BR-INV-04-07: valuation is a per-LOCATION decision. */}
                    <NativeSelect
                      id="wh-valuation"
                      value={form.valuationMethod}
                      disabled={!editable}
                      onChange={(event) =>
                        set('valuationMethod', event.target.value as FormState['valuationMethod'])
                      }
                    >
                      {VALUATION_METHODS.map((method) => (
                        <option key={method} value={method}>
                          {t(`wh.valuation.${method}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <SwitchField
                    id="wh-negative"
                    label={t('wh.fAllowNegative')}
                    hint={t('wh.allowNegativeHint')}
                    checked={form.allowNegativeStock}
                    onChange={(next) => set('allowNegativeStock', next)}
                    disabled={!editable}
                  />
                  <SwitchField
                    id="wh-available"
                    label={t('wh.fIncludeAvailable')}
                    hint={t('wh.includeAvailableHint')}
                    checked={form.includeInAvailable}
                    onChange={(next) => set('includeInAvailable', next)}
                    disabled={!editable}
                  />
                  {/* ERR-MD-08-05 — "Ticked a second receiving default →
                      'Central Distribution Center is already the receiving
                      default. Make Downtown Retail Store the default
                      instead?' → Say yes or no." The server has held the
                      single-default model all along (BR-MD-08-04's partial
                      unique index; the service's clearDefault) — this switch
                      is the screen half, and the question is asked HERE, so
                      the tick lands only on yes. The save then tells the
                      server, which unticks the previous atomically either
                      way. The md14 tiers screen's pattern, location-shaped. */}
                  <SwitchField
                    id="wh-default"
                    label={t('wh.fDefault')}
                    hint={t('wh.defaultHint')}
                    checked={form.isDefault}
                    onChange={(next) => {
                      if (next && !form.isDefault && currentDefault !== undefined) {
                        setConfirmingDefault(true);
                        return;
                      }
                      set('isDefault', next);
                    }}
                    disabled={!editable}
                  />
                  {/* IT-L4 — the colour the Items list tints this place's
                      quantity chip with: the six GEN-170 accents as quick
                      picks and any #rrggbb off the native picker, the
                      categories screen's own control. */}
                  <div className="grid gap-1.5">
                    <span id="wh-colour" className="text-sm font-medium">
                      {t('wh.fColour')}
                    </span>
                    <div role="radiogroup" aria-labelledby="wh-colour" className="flex flex-wrap items-center gap-2">
                      {(
                        [
                          '',
                          ...CATEGORY_COLOURS,
                          ...(form.colour !== '' &&
                          !(CATEGORY_COLOURS as readonly string[]).includes(form.colour)
                            ? [form.colour]
                            : []),
                        ] as readonly string[]
                      ).map((colour) => {
                        const chosen = form.colour === colour;
                        return (
                          <button
                            key={colour || 'none'}
                            type="button"
                            role="radio"
                            aria-checked={chosen}
                            aria-label={colour === '' ? t('wh.noColour') : colour}
                            title={colour === '' ? t('wh.noColour') : colour}
                            disabled={!editable}
                            onClick={() => set('colour', colour)}
                            className={cn(
                              'size-8 rounded-full border-2 text-xs text-muted-foreground',
                              chosen ? 'border-foreground' : 'border-transparent',
                              colour === '' && 'border-dashed border-input',
                            )}
                            style={colour === '' ? undefined : { backgroundColor: colour }}
                          >
                            {colour === '' ? '—' : null}
                          </button>
                        );
                      })}
                      <input
                        id="wh-colour-custom"
                        type="color"
                        aria-label={t('wh.customColour')}
                        title={t('wh.customColour')}
                        disabled={!editable}
                        className="size-8 cursor-pointer rounded-full border-2 border-dashed border-input bg-transparent p-0.5"
                        value={form.colour === '' ? '#0e9aa1' : form.colour}
                        onChange={(event) => set('colour', event.target.value.toLowerCase())}
                      />
                    </div>
                  </div>
                </div>
                {confirmingDefault && currentDefault !== undefined ? (
                  <ConfirmBox
                    open
                    title={t('wh.defaultSwapTitle', {
                      name: form.name.trim() || (selected?.name ?? ''),
                    })}
                    body={t('wh.defaultSwapBody', { current: currentDefault.name })}
                    confirmLabel={t('wh.makeDefault')}
                    onConfirm={() => {
                      set('isDefault', true);
                      setConfirmingDefault(false);
                    }}
                    onCancel={() => setConfirmingDefault(false)}
                  />
                ) : null}
              </CardContent>
            </Card>

            {/* ── Capacity & structure (optional depth) ─────────────────── */}
            <Section title={t('wh.secCapacity')} hint={t('wh.secCapacityHint')}>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="wh-capacity"
                  label={t('wh.fCapacity')}
                  value={form.capacity}
                  onChange={(next) => set('capacity', next)}
                  disabled={!editable}
                />
                <div className="grid gap-1.5">
                  <Label htmlFor="wh-capacity-unit">{t('wh.fCapacityUnit')}</Label>
                  <NativeSelect
                    id="wh-capacity-unit"
                    value={form.capacityUnit}
                    disabled={!editable}
                    onChange={(event) => set('capacityUnit', event.target.value)}
                  >
                    <option value="">—</option>
                    {CAPACITY_UNITS.map((unit) => (
                      <option key={unit} value={unit}>
                        {t(`wh.capacityUnit.${unit}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              </div>

              <div className="grid gap-2">
                <h4 className="text-sm font-semibold">{t('wh.zones')}</h4>
                {zones.map((zone, index) => (
                  <div key={index} className="flex flex-wrap items-end gap-2">
                    <Input
                      aria-label={t('wh.colZoneCode')}
                      placeholder={t('wh.colZoneCode')}
                      className="h-10 w-28 font-mono text-sm"
                      value={zone.code}
                      disabled={!editable}
                      onChange={(event) => {
                        const next = [...zones];
                        next[index] = { ...zone, code: event.target.value };
                        setZones(next);
                        renameZoneOnBins(index, event.target.value);
                      }}
                    />
                    <Input
                      aria-label={t('wh.colZoneName')}
                      placeholder={t('wh.colZoneName')}
                      className="h-10 w-40 text-sm"
                      value={zone.name}
                      disabled={!editable}
                      onChange={(event) => {
                        const next = [...zones];
                        next[index] = { ...zone, name: event.target.value };
                        setZones(next);
                      }}
                    />
                    <NativeSelect
                      aria-label={t('wh.colZoneType')}
                      className="h-10 w-36 text-sm"
                      value={zone.type}
                      disabled={!editable}
                      onChange={(event) => {
                        const next = [...zones];
                        next[index] = { ...zone, type: event.target.value as ZoneRow['type'] };
                        setZones(next);
                      }}
                    >
                      {ZONE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`wh.zoneType.${type}`)}
                        </option>
                      ))}
                    </NativeSelect>
                    {/*
                      A zone row and a bin row both carry a box the spec calls
                      "Capacity", and BR-INV-04-09 puts both tables on the same
                      warehouse screen. Two controls with one accessible name is
                      the same defect the item form's two "Unit" columns had:
                      the placeholder keeps the spec's word, the accessible name
                      says which table and which row.
                    */}
                    <Input
                      aria-label={t('wh.cellZoneCapacity', { line: index + 1 })}
                      placeholder={t('wh.colCapacity')}
                      dir="ltr"
                      inputMode="numeric"
                      className="h-10 w-28 font-mono text-sm"
                      value={zone.capacity}
                      disabled={!editable}
                      onChange={(event) => {
                        const next = [...zones];
                        next[index] = { ...zone, capacity: event.target.value };
                        setZones(next);
                      }}
                    />
                    {editable ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('common.remove')}
                        onClick={() => removeZone(index)}
                      >
                        <X aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                ))}
                {editable ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="justify-self-start border-dashed"
                    onClick={() =>
                      setZones([
                        ...zones,
                        { code: '', name: '', type: 'other', capacity: '', binCount: 0, boundCode: '' },
                      ])
                    }
                  >
                    <Plus aria-hidden /> {t('wh.addZone')}
                  </Button>
                ) : null}
              </div>

              <SwitchField
                id="wh-use-bins"
                label={t('wh.fUseBins')}
                checked={form.useBins}
                onChange={(next) => set('useBins', next)}
                disabled={!editable}
              />
              {/* BR-INV-04-11: switching bins off HIDES the table; the rows
                  stay in state and go back with the next save. */}
              {form.useBins ? (
                <div className="grid gap-2">
                  <h4 className="text-sm font-semibold">{t('wh.bins')}</h4>
                  {bins.map((bin, index) => (
                    <div key={index} className="flex flex-wrap items-end gap-2">
                      <Input
                        aria-label={t('wh.colBinCode')}
                        placeholder={t('wh.colBinCode')}
                        className="h-10 w-32 font-mono text-sm"
                        value={bin.code}
                        disabled={!editable}
                        onChange={(event) => {
                          const next = [...bins];
                          next[index] = { ...bin, code: event.target.value };
                          setBins(next);
                        }}
                      />
                      <NativeSelect
                        aria-label={t('wh.colBinZone')}
                        className="h-10 w-40 text-sm"
                        value={bin.zoneCode}
                        disabled={!editable}
                        onChange={(event) => {
                          const next = [...bins];
                          next[index] = { ...bin, zoneCode: event.target.value };
                          setBins(next);
                        }}
                      >
                        <option value="">{t('wh.binNoZone')}</option>
                        {zoneCodes.map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </NativeSelect>
                      <Input
                        aria-label={t('wh.cellBinCapacity', { line: index + 1 })}
                        placeholder={t('wh.colCapacity')}
                        dir="ltr"
                        inputMode="numeric"
                        className="h-10 w-28 font-mono text-sm"
                        value={bin.capacity}
                        disabled={!editable}
                        onChange={(event) => {
                          const next = [...bins];
                          next[index] = { ...bin, capacity: event.target.value };
                          setBins(next);
                        }}
                      />
                      {editable ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('common.remove')}
                          onClick={() => setBins(bins.filter((_, at) => at !== index))}
                        >
                          <X aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="justify-self-start border-dashed"
                      onClick={() => setBins([...bins, { code: '', zoneCode: '', capacity: '' }])}
                    >
                      <Plus aria-hidden /> {t('wh.addBin')}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('wh.binsOffNote')}</p>
              )}
            </Section>

            {/* ── Transfer & logistics ──────────────────────────────────── */}
            <Section title={t('wh.secLogistics')} hint={t('wh.secLogisticsHint')}>
              <div className="grid gap-2">
                <Label>{t('wh.fCarriers')}</Label>
                <div className="flex flex-wrap gap-2">
                  {CARRIERS.map((carrier) => (
                    <Chip
                      key={carrier}
                      active={form.carriers.includes(carrier)}
                      onClick={() => {
                        if (!editable) return;
                        set(
                          'carriers',
                          form.carriers.includes(carrier)
                            ? form.carriers.filter((entry) => entry !== carrier)
                            : [...form.carriers, carrier],
                        );
                      }}
                    >
                      {carrier}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="wh-lead-time"
                  label={t('wh.fLeadTime')}
                  value={form.leadTimeDays}
                  onChange={(next) => set('leadTimeDays', next.replace(/[^\d]/g, ''))}
                  disabled={!editable}
                />
                <SwitchField
                  id="wh-transfers"
                  label={t('wh.fAllowTransfers')}
                  checked={form.allowTransfers}
                  onChange={(next) => set('allowTransfers', next)}
                  disabled={!editable}
                />
              </div>
            </Section>
          </div>
        )}
      </div>

      <LegalEntitiesCard />
    </div>
  );
}

/**
 * The Companies list (MD-08 field 4). Usually one row — the entity seeded at
 * signup, named after the company; a group adds its second company here and
 * the field-4 picker appears on every warehouse. The last active entity can
 * be neither deleted nor switched off (field 4 is Required), and an entity
 * that owns warehouses refuses deletion with MD-RULE-4's counted sentence.
 */
function LegalEntitiesCard() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: entities } = useLegalEntities(true);
  // The row whose Delete was pressed — one box for the whole list, so it has
  // to carry the record rather than a flag.
  const [deleting, setDeleting] = useState<NonNullable<typeof entities>[number] | null>(null);
  const { create, update, setActive, remove } = useLegalEntityMutations();

  const fail = (cause: unknown) =>
    setError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('entities.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm text-muted-foreground">{t('entities.hint')}</p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="le-new">{t('entities.fName')}</Label>
            <Input
              id="le-new"
              maxLength={120}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <Gated permission="inventory.create" reasonKey="perm.cannot.createEntities">
            {/* NOT common.new: the warehouses header already owns "+ New" on
                this screen — one accessible name per screen per action, the
                permission-UI wave's aria-label lesson (third occurrence of
                the class; nine journeys strict-mode-failed on the units
                sibling of this button). */}
            <Button
              variant="outline"
              disabled={!name.trim()}
              onClick={() => {
                setError(null);
                create
                  .mutateAsync({ name: name.trim() })
                  .then(() => setName(''))
                  .catch(fail);
              }}
            >
              <Plus aria-hidden /> {t('entities.new')}
            </Button>
          </Gated>
        </div>
        <div className="grid gap-2">
          {(entities ?? []).map((entity) => (
            <div key={entity.id} className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2">
              {editingId === entity.id ? (
                <Input
                  aria-label={t('entities.renameNamed', { name: entity.name })}
                  maxLength={120}
                  className="max-w-[280px]"
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                />
              ) : (
                <span className="font-medium">{entity.name}</span>
              )}
              <span className="text-xs text-muted-foreground">
                {t('entities.owns', { count: entity.warehousesOwned })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t(entity.isActive ? 'common.active' : 'common.inactive')}
              </span>
              <div className="ms-auto flex gap-2">
                {editingId === entity.id ? (
                  <>
                    <Gated permission="inventory.edit" reasonKey="perm.cannot.editEntities">
                      <Button
                        size="sm"
                        disabled={!editName.trim()}
                        aria-label={t('entities.saveNamed', { name: entity.name })}
                        onClick={() => {
                          setError(null);
                          update
                            .mutateAsync({ entityId: entity.id, body: { name: editName.trim() } })
                            .then(() => setEditingId(null))
                            .catch(fail);
                        }}
                      >
                        {t('common.save')}
                      </Button>
                    </Gated>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('entities.cancelNamed', { name: entity.name })}
                      onClick={() => setEditingId(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Gated permission="inventory.edit" reasonKey="perm.cannot.editEntities">
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={t('entities.editNamed', { name: entity.name })}
                        onClick={() => {
                          setEditingId(entity.id);
                          setEditName(entity.name);
                          setError(null);
                        }}
                      >
                        {t('common.edit')}
                      </Button>
                    </Gated>
                    <Gated permission="inventory.edit" reasonKey="perm.cannot.editEntities">
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={t('entities.switchNamed', { name: entity.name })}
                        onClick={() => {
                          setError(null);
                          setActive
                            .mutateAsync({ entityId: entity.id, active: !entity.isActive })
                            .catch(fail);
                        }}
                      >
                        {t(entity.isActive ? 'items.switchOff' : 'items.switchOn')}
                      </Button>
                    </Gated>
                    {entity.warehousesOwned === 0 ? (
                      <Gated permission="inventory.delete" reasonKey="perm.cannot.deleteEntities">
                        <Button
                          size="sm"
                          variant="destructive"
                          aria-label={t('entities.deleteNamed', { name: entity.name })}
                          // §4.20: even an unused record asks first — GEN-58's
                          // sentence, in the shared box (GEN-204/GEN-209).
                          onClick={() => setDeleting(entity)}
                        >
                          {t('common.delete')}
                        </Button>
                      </Gated>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <DeleteConfirmBox
          open={deleting !== null}
          detail={deleting === null ? null : t('entities.confirmDelete', { name: deleting.name })}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const entity = deleting;
            setDeleting(null);
            if (entity === null) return;
            setError(null);
            remove.mutateAsync({ entityId: entity.id }).catch(fail);
          }}
          busy={remove.isPending}
        />
      </CardContent>
    </Card>
  );
}
